import React, { useCallback } from 'react';
import { useRecordDockState } from './record-dock.hooks';
import { LiveWaveform } from './LiveWaveform';
import { useDockDrag } from '../dock-animations/use-dock-drag';
import {
  DockWrapper,
  DockHandleWrapper,
  DockHandle,
  RecordingDot,
  RecordingHeader,
  RecordingTitle,
  RecordingTimer,
  RecordingMiddle,
  DockLabel,
  DockIcon,
  DockItem,
  RecordingBottom,
  RecordingWrapper,
  RerecordConfirmation,
  DiscardAndReRecordTitleSpan,
  DiscardAndReRecordDescriptionSpan,
  CTAWrapper,
  CancelButton,
  ReRecordButton,
  ModalOverlay,
  ModalContent,
  DiscardButton,
  TitleAndCloseWrapper,
} from './record-dock.styled';

interface RecordDockProps {
  onClose?: () => void;
  onSend?: (audioBlob: Blob, files?: File[], transcript?: string) => void;
  animateInOnMount?: boolean;
}

export const RecordDock: React.FC<RecordDockProps> = ({
  onClose,
  onSend,
  animateInOnMount = false,
}) => {
  const {
    recordingState,
    recordingComplete,
    pauseRecording,
    resumeRecording,
    handleStopAndSend,
    handleReRecord,
    cancelRecording,
    formatTime,
    maxRecordingDuration,
    getTimerColor,
  } = useRecordDockState(onSend, onClose);

  const [showReRecordModal, setShowReRecordModal] = React.useState(false);
  const [showCloseModal, setShowCloseModal] = React.useState(false);
  const [waveformResetTrigger, setWaveformResetTrigger] = React.useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = React.useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = React.useState(animateInOnMount);

  // Ref so the drag callback always sees current recording state
  const recordingStateRef = React.useRef(recordingState);
  recordingStateRef.current = recordingState;

  const {
    isDragging,
    handleProps: dragHandleProps,
    dragStyle,
    setDragOffsetY,
  } = useDockDrag({
    onDismiss: useCallback(() => {
      const rs = recordingStateRef.current;
      if (rs.isRecording || rs.isPaused) {
        // Recording active — reset drag position, show confirmation instead
        setDragOffsetY(0);
        if (rs.isRecording && !rs.isPaused) pauseRecording();
        setShowCloseModal(true);
      } else {
        // Not recording — just close
        cancelRecording();
        onClose?.();
      }
    }, [cancelRecording, onClose, pauseRecording]),
  });

  React.useEffect(() => {
    if (animateInOnMount) {
      const timer = setTimeout(() => setIsAnimatingIn(false), 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [animateInOnMount]);

  const onStopClick = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      handleStopAndSend();
      onClose?.();
    }, 300);
  };

  const handleClose = () => {
    if (recordingState.isRecording && !recordingState.isPaused) {
      // Automatically pause recording when handle is clicked
      pauseRecording();
      setShowCloseModal(true);
    } else if (recordingState.isPaused) {
      setShowCloseModal(true);
    } else {
      // Not recording, just close normally
      setIsAnimatingOut(true);
      setTimeout(() => {
        cancelRecording();
        onClose?.();
      }, 300);
    }
  };

  return (
    <>
      <DockWrapper
        isAnimatingOut={isAnimatingOut}
        isAnimatingIn={isAnimatingIn}
        style={dragStyle}
      >
        <DockHandleWrapper {...dragHandleProps}>
          <DockHandle onClick={() => { if (!isDragging.current) handleClose(); }} style={{ cursor: 'grab' }} />
        </DockHandleWrapper>
            <RecordingWrapper>
              <RecordingHeader>
                <RecordingDot />
                <RecordingTitle>Recording</RecordingTitle>
              </RecordingHeader>
              <RecordingTimer>
                <span style={{ color: getTimerColor() }}>
                  {formatTime(recordingState.recordingTime)}
                </span>
                <span style={{ color: '#8F95A3' }}> / {formatTime(maxRecordingDuration)}</span>
              </RecordingTimer>
            </RecordingWrapper>
            <RecordingMiddle>
              <img
                src="/cancel-voice.svg"
                alt="Cancel"
                onClick={handleClose}
                style={{ cursor: 'pointer' }}
              />
              <LiveWaveform
                barWidth={2.8}
                minHeight={2}
                maxHeight={45}
                speed={1}
                amplitudeBoost={1}
                paused={recordingState.isPaused}
                resetTrigger={waveformResetTrigger}
                recordingActive={recordingState.isRecording && !recordingComplete && !recordingState.isPaused}
              />
            </RecordingMiddle>

        <RecordingBottom>
          {recordingState.isPaused ? (
            <DockItem type="button" onClick={resumeRecording}>
              <DockIcon src="/icons/onboarding/question/resume.svg" alt="Resume" />
              <DockLabel>Resume</DockLabel>
            </DockItem>
          ) : (
            <DockItem type="button" onClick={pauseRecording}>
              <DockIcon src="/icons/onboarding/question/pause.svg" alt="Pause" />
              <DockLabel>Pause</DockLabel>
            </DockItem>
          )}

          <DockItem type="button" onClick={onStopClick}>
            <DockIcon src="/icons/onboarding/question/stop-button.svg" alt="Stop" />
          </DockItem>

          <DockItem
            type="button"
            onClick={() => {
              pauseRecording();
              setShowReRecordModal(true);
            }}
          >
            <DockIcon src="/icons/onboarding/question/re-record.svg" alt="Re-record" />
            <DockLabel>Re-record</DockLabel>
          </DockItem>
        </RecordingBottom>
      </DockWrapper>
      <ModalOverlay visible={showReRecordModal} onClick={() => setShowReRecordModal(false)}>
        <ModalContent visible={showReRecordModal}>
          <RerecordConfirmation className={showReRecordModal ? 'visible' : ''}>
            <DiscardAndReRecordTitleSpan>Discard and re-record?</DiscardAndReRecordTitleSpan>
            <DiscardAndReRecordDescriptionSpan>
              You’ll begin again. Just remember it doesn’t have to be perfect. Stream of
              consciousness is just fine.
            </DiscardAndReRecordDescriptionSpan>
            <CTAWrapper>
              <CancelButton onClick={() => setShowReRecordModal(false)}>Cancel</CancelButton>

              <ReRecordButton
                onClick={() => {
                  setShowReRecordModal(false);
                  setWaveformResetTrigger((prev) => prev + 1);
                  handleReRecord();
                }}
              >
                Re-record
              </ReRecordButton>
            </CTAWrapper>
          </RerecordConfirmation>
        </ModalContent>
      </ModalOverlay>
      <ModalOverlay
        visible={showCloseModal}
        onClick={() => {
          setShowCloseModal(false);
          if (recordingState.isPaused) {
            resumeRecording();
          }
        }}
      >
        <ModalContent visible={showCloseModal}>
          <RerecordConfirmation className={showCloseModal ? 'visible' : ''}>
            <TitleAndCloseWrapper>
              <DiscardAndReRecordTitleSpan>Discard your recording?</DiscardAndReRecordTitleSpan>
            </TitleAndCloseWrapper>
            <DiscardAndReRecordDescriptionSpan>
              You will lose your progress. You can start again anytime.
            </DiscardAndReRecordDescriptionSpan>
            <CTAWrapper style={{ gap: '24px' }}>
              <DiscardButton
                onClick={() => {
                  setShowCloseModal(false);
                  if (recordingState.isPaused) {
                    resumeRecording();
                  }
                }}
              >
                Continue
              </DiscardButton>
              <ReRecordButton
                onClick={async () => {
                  setShowCloseModal(false);
                  setIsAnimatingOut(true);
                  setTimeout(() => {
                    onClose?.();
                  }, 300);
                }}
              >
                Discard
              </ReRecordButton>
            </CTAWrapper>
          </RerecordConfirmation>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};
